import { PropRuntimeNode } from '../../../../../shared/types/PropRuntimeGraph';
import {
    ActionAccelerateNode,
    ActionCollectNode,
    ActionEliminateNode,
    ActionExtendCommissionNode,
    ActionExtraYieldNode,
    ActionGrowNode,
    ActionHarvestNode,
    ActionPermanentBoostNode,
    ActionProtectNode,
    ActionPurifyNode,
    ActionRarityWeightNode,
    ActionSpawnNode,
    ActionSwapPositionNode,
    ActionTransformNode,
    ActionTriggerVisualNode,
} from './ActionRuntimeNodes';
import {
    FloatOperationNode,
    IntOperationNode,
    RoundNumberNode,
} from './CalculationRuntimeNodes';
import {
    LogicCompareFloatNode,
    LogicCompareIntNode,
    LogicContainsNode,
    LogicProbabilityNode,
} from './ConditionRuntimeNodes';
import {
    EventAcquireNode,
    EventEliminatedNode,
    EventTurnStartNode,
    EventUseNode,
} from './EventRuntimeNodes';
import {
    FilterPrototypeNode,
    FilterRandomNode,
    FilterRarityNode,
    FilterTagNode,
    TargetFilterNode,
} from './FilterRuntimeNodes';
import {
    RuntimeNodeClassMap,
    RuntimeNodeContext,
    RuntimeNodeLike,
} from './RuntimeNodeTypes';
import {
    PrototypeLibraryNode,
    PrototypeSelectNode,
    TargetBoardNode,
    TargetSelfNode,
} from './TargetRuntimeNodes';
import {
    FloatAttributeNode,
    FloatConstantNode,
    FloatRandomNode,
    IntAttributeNode,
    IntConstantNode,
    IntKindCountNode,
    IntQuantityNode,
    IntRandomNode,
    RarityConstantNode,
    TagConstantNode,
} from './ValueRuntimeNodes';

/**
 * 运行时节点 class 注册表。
 *
 * 每个 PropRuntimeNodeType 必须对应一个 class；新增节点时应在这里注册。
 */
export const runtimeNodeClassMap: RuntimeNodeClassMap = {
    eventTurnStart: EventTurnStartNode,
    eventAcquire: EventAcquireNode,
    eventUse: EventUseNode,
    eventEliminated: EventEliminatedNode,
    targetSelf: TargetSelfNode,
    targetBoard: TargetBoardNode,
    prototypeLibrary: PrototypeLibraryNode,
    prototypeSelect: PrototypeSelectNode,
    targetFilter: TargetFilterNode,
    filterRarity: FilterRarityNode,
    filterTag: FilterTagNode,
    filterPrototype: FilterPrototypeNode,
    filterRandom: FilterRandomNode,
    actionGrow: ActionGrowNode,
    actionExtraYield: ActionExtraYieldNode,
    actionPurify: ActionPurifyNode,
    actionProtect: ActionProtectNode,
    actionAccelerate: ActionAccelerateNode,
    actionTransform: ActionTransformNode,
    actionSpawn: ActionSpawnNode,
    actionEliminate: ActionEliminateNode,
    actionCollect: ActionCollectNode,
    actionHarvest: ActionHarvestNode,
    actionRarityWeight: ActionRarityWeightNode,
    actionExtendCommission: ActionExtendCommissionNode,
    actionSwapPosition: ActionSwapPositionNode,
    actionPermanentBoost: ActionPermanentBoostNode,
    actionTriggerVisual: ActionTriggerVisualNode,
    intConstant: IntConstantNode,
    intAttribute: IntAttributeNode,
    intQuantity: IntQuantityNode,
    intKindCount: IntKindCountNode,
    intRandom: IntRandomNode,
    intOperation: IntOperationNode,
    floatConstant: FloatConstantNode,
    floatAttribute: FloatAttributeNode,
    floatRandom: FloatRandomNode,
    floatOperation: FloatOperationNode,
    roundNumber: RoundNumberNode,
    rarityConstant: RarityConstantNode,
    tagConstant: TagConstantNode,
    logicCompareInt: LogicCompareIntNode,
    logicCompareFloat: LogicCompareFloatNode,
    logicContains: LogicContainsNode,
    logicProbability: LogicProbabilityNode,
};

/**
 * 根据运行时节点定义创建具体节点 class 实例。
 *
 * @param node 运行时节点定义。
 * @param context 当前执行上下文。
 * @returns 对应节点实例；未注册类型返回 undefined。
 */
export function createRuntimeNode(
    node: PropRuntimeNode,
    context: RuntimeNodeContext,
): RuntimeNodeLike | undefined {
    const NodeClass = runtimeNodeClassMap[node.type];
    return NodeClass ? new NodeClass(node, context) : undefined;
}
